import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AccountDetail, AIReplySettings } from '../types';
import {
  getAccountDetails,
  updateAccountStatus,
  deleteAccount,
  generateQRLogin,
  checkQRLoginStatus,
  updateAccountRemark,
  updateAccountAutoConfirm,
  updateAccountPauseDuration,
  updateAccountShowBrowser,
  updateAccountCookie,
  updateAccountLoginInfo,
  updateAccountAISettings,
  getAllAISettings,
  getAccountAISettings,
  startManualVerify,
  getManualVerifyStatus,
  confirmManualVerify,
  cancelManualVerify
} from '../services/api';
import {
  Plus, Edit2, Trash2, QrCode, X, Check, Loader2,
  MessageSquare, RefreshCw, Save, User, Clock, MessageCircle,
  Upload, Key, Eye, EyeOff, Bot, Settings, ShieldCheck, AlertTriangle
} from 'lucide-react';
import toast from '../lib/toast';

type ModalType = 'edit' | 'ai-settings' | null;

/**
 * 账号状态徽章：以「真实可用」为准
 * 真实在线 = WebSocket connected + 已拿到 token + 心跳未超时（后端 healthy 字段）
 * 数据来源：/cookies/details 的 connection_* / healthy / *_age
 */
const fmtAge = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return '无记录';
  if (seconds < 60) return `${Math.round(seconds)} 秒前`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} 分钟前`;
  return `${Math.round(seconds / 3600)} 小时前`;
};

const ConnectionBadge: React.FC<{ account: AccountDetail }> = ({ account }) => {
  const base = 'px-2.5 py-0.5 rounded-lg text-xs font-bold whitespace-nowrap cursor-help';
  const state = account.connection_state;
  const detail = account.connection_detail || '';

  const tip = [
    detail,
    `最近心跳：${fmtAge(account.heartbeat_age)}`,
    `最近换 token：${fmtAge(account.token_age)}`,
    `最近收消息：${fmtAge(account.message_age)}`,
  ].filter(Boolean).join(' ｜ ');

  // 【优先】需重新扫码登录：滑块验证失败已停机，即使账号被标记为禁用也要明确提示去重新扫码
  if (account.needs_manual_verify) {
    return (
      <span className={`${base} bg-red-100 text-red-600`} title={tip || '滑块验证无法通过，账号已停机，请点击卡片上的「重新扫码登录」换新 Cookie 后重新启用'}>
        需重新扫码登录
      </span>
    );
  }
  if (!account.enabled) {
    return <span className={`${base} bg-gray-100 text-gray-500`} title="账号已禁用，不接收消息">已停用</span>;
  }
  if (state === 'connected') {
    // 【令牌过期可见】连接在线 ≠ 能发消息：服务端已判 Session 过期时，优先提示需重新登录。
    if (account.session_expired) {
      return (
        <span
          className={`${base} bg-amber-100 text-amber-700`}
          title={`连接在线，但登录令牌已过期，自动回复发不出消息，请重新扫码登录。${tip}`}
        >
          在线·令牌过期
        </span>
      );
    }
    // healthy === false 表示连上了但心跳/任务异常（僵尸连接），不能算真正的在线
    if (account.healthy === false) {
      return <span className={`${base} bg-amber-100 text-amber-700`} title={tip}>在线·心跳异常</span>;
    }
    return <span className={`${base} bg-green-100 text-green-700`} title={tip || '已连接，可正常收发消息'}>在线</span>;
  }
  if (state === 'connecting' || state === 'reconnecting') {
    return <span className={`${base} bg-amber-100 text-amber-700`} title={tip}>连接中</span>;
  }
  if (state === 'failed') {
    return <span className={`${base} bg-red-100 text-red-600`} title={tip || '连接失败，请检查登录态或重新扫码'}>登录失效</span>;
  }
  if (!state || state === 'unknown') {
    // 【修复】此前这里渲染成绿色「在线」，但 title 却写着「未获取到连接状态」——
    // 自相矛盾且危险：后端取不到连接信息时所有账号都会是 unknown，
    // 界面上一片「在线」，实际可能全部掉线，漏发漏回都发现不了。
    return <span className={`${base} bg-gray-100 text-gray-600`} title="未获取到连接状态，请确认账号是否已启动">状态未知</span>;
  }
  return <span className={`${base} bg-gray-100 text-gray-500`} title={tip || '未连接'}>未连接</span>;
};

const AccountList: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [qrStatus, setQrStatus] = useState<string>('pending');
  const [qrErrorMsg, setQrErrorMsg] = useState<string>('');
  // 人工验证（可见浏览器过风控）
  const [mvAccount, setMvAccount] = useState<AccountDetail | null>(null);
  // 「刷新凭证」二选一弹窗：扫码换 Cookie（优先）+ 人工验证（过风控兜底）。null 表示未打开。
  const [refreshAccount, setRefreshAccount] = useState<AccountDetail | null>(null);
  const [mvStatus, setMvStatus] = useState<string>('idle');
  const [mvMessage, setMvMessage] = useState<string>('');
  // 人工验证：点「验证完成」时后端检测到浏览器里其实没登录成功，给一条黄色警告（不拦截）
  const [mvWarning, setMvWarning] = useState<string>('');
  // 人工验证远程投屏地址：后端开启远程投屏时返回，前端内嵌 iframe 实时查看/操作服务器浏览器
  // （部署在无头/远程服务器时，本地可见浏览器看不到，必须走投屏）
  const [mvControlUrl, setMvControlUrl] = useState<string>('');
  // 人工验证模式：repair=账号已掉线的救火；refresh=账号仍在线时的预防性保养
  // 两种模式走同一套可见浏览器流程，区别只在校验强度与文案
  const [mvMode, setMvMode] = useState<'repair' | 'refresh'>('repair');
  // 本次刷新实际发生变化的凭证字段，成功后展示给用户核对
  const [mvChanged, setMvChanged] = useState<string[]>([]);
  const mvPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 成功后延迟关闭弹窗的句柄：挂在 ref 上，避免组件卸载后仍触发 setState
  const mvCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 扫码轮询/延时句柄：必须挂在 ref 上，否则关弹窗或卸载组件后 interval 仍在跑
  // （每个 2 秒打一次后端，且会反复 setState），重复点「扫码添加」还会叠加多个 interval。
  const qrPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [editingAccount, setEditingAccount] = useState<AccountDetail | null>(null);

  // 编辑表单状态
  const [editForm, setEditForm] = useState({
    remark: '',
    cookie: '',
    auto_confirm: false,
    pause_duration: 0,
    username: '',
    login_password: '',
    // show_browser 不放进表单：它由独立开关单独保存，避免保存其他字段时被连带覆盖
    showLoginPassword: false,
  });
  // 「显示浏览器」独立开关的提交中状态
  const [togglingBrowser, setTogglingBrowser] = useState(false);

  // AI设置表单状态
  const [aiSettings, setAiSettings] = useState<AIReplySettings>({
    ai_enabled: false,
    max_discount_percent: 10,
    max_discount_amount: 100,
    max_bargain_rounds: 3,
    custom_prompts: '',
  });
  const [saving, setSaving] = useState(false);

  // silent=true 时用于后台轮询：不显示 loading，避免页面闪烁
  const loadAccounts = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getAccountDetails();

      // 获取所有账号的AI设置
      let allAISettings: Record<string, AIReplySettings> = {};
      try {
        allAISettings = await getAllAISettings();
      } catch (e) {
        console.error('Failed to load AI settings:', e);
      }

      // 合并AI设置到账号数据
      const accountsWithAI = data.map(account => ({
        ...account,
        ai_enabled: allAISettings[account.id]?.ai_enabled ?? false,
        max_discount_percent: allAISettings[account.id]?.max_discount_percent ?? 10,
        max_discount_amount: allAISettings[account.id]?.max_discount_amount ?? 100,
        max_bargain_rounds: allAISettings[account.id]?.max_bargain_rounds ?? 3,
        custom_prompts: allAISettings[account.id]?.custom_prompts ?? '',
      }));

      setAccounts(accountsWithAI);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // 停止扫码轮询与成功后的自动关窗延时（幂等，可重复调用）
  const stopQRPolling = () => {
    if (qrPollTimerRef.current !== null) {
      clearInterval(qrPollTimerRef.current);
      qrPollTimerRef.current = null;
    }
    if (qrCloseTimerRef.current !== null) {
      clearTimeout(qrCloseTimerRef.current);
      qrCloseTimerRef.current = null;
    }
  };

  // 关闭扫码弹窗：先停轮询再关，避免后台还在打接口
  const closeQRModal = () => {
    stopQRPolling();
    setShowQRModal(false);
    setRefreshAccount(null);
  };

  // 二维码展示块：被「扫码添加新账号」独立弹窗与「刷新凭证」弹窗共用，避免重复渲染逻辑。
  // onGenerate 由调用方决定扫码是独立弹窗(openStandalone=true)还是内嵌刷新弹窗(openStandalone=false)。
  // qrStatus 为 pending/idle 时（尚未生成）展示「生成二维码」按钮；其余状态展示二维码/进度/结果。
  const renderQRBox = (onGenerate: () => void) => {
    const idle = qrStatus === 'pending' || qrStatus === 'idle';
    if (idle) {
      return (
        <button
          onClick={onGenerate}
          className="ios-btn-primary flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 transition-transform hover:scale-105 active:scale-95 mx-auto my-8"
        >
          <QrCode className="w-5 h-5" />
          生成二维码
        </button>
      );
    }
    return (
      <div className="w-64 h-64 bg-[#F7F8FA] rounded-[2rem] mx-auto flex items-center justify-center overflow-hidden border-4 border-white shadow-inner mb-8 relative">
        {qrStatus === 'loading' && <Loader2 className="w-10 h-10 text-[#FFE815] animate-spin" />}
        {qrStatus === 'waiting' && <img src={qrCodeUrl} alt="QR Code" className="w-full h-full p-2" />}
        {qrStatus === 'processing' && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-gray-900 animate-fade-in">
            <Loader2 className="w-10 h-10 text-[#FFE815] animate-spin mb-4" />
            <span className="font-bold text-lg">已确认，正在完成登录</span>
            <span className="text-xs text-gray-500 mt-2">正在获取登录凭证并重连账号，请稍候…</span>
          </div>
        )}
        {qrStatus === 'success' && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-green-600 animate-fade-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8" />
            </div>
            <span className="font-bold text-lg">登录成功</span>
          </div>
        )}
        {qrStatus === 'error' && (
          <div className="flex flex-col items-center px-4">
            <span className="text-red-500 font-bold mb-2">登录失败</span>
            {qrErrorMsg && <span className="text-xs text-gray-500 mb-3 leading-relaxed">{qrErrorMsg}</span>}
            <button onClick={onGenerate} className="text-xs bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1 hover:bg-gray-300"><RefreshCw className="w-3 h-3" /> 重试</button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    loadAccounts();
    // 每 10 秒静默刷新一次，让「在线/连接中/需人工验证」反映真实状态
    const timer = setInterval(() => loadAccounts(true), 10000);
    return () => clearInterval(timer);
  }, []);

  // 组件卸载时兜底清理，防止切换页面后定时器泄漏
  useEffect(() => () => stopQRPolling(), []);

  // 同上：人工验证成功后的延迟关窗定时器也要清理
  useEffect(() => () => {
    if (mvCloseTimerRef.current !== null) {
      clearTimeout(mvCloseTimerRef.current);
      mvCloseTimerRef.current = null;
    }
  }, []);

  const handleDelete = async (id: string) => {
    if (confirm('确认删除该账号吗？')) {
      await deleteAccount(id);
      loadAccounts();
    }
  };

  const openEditModal = (account: AccountDetail) => {
    setEditingAccount(account);
    setEditForm({
      remark: account.remark || account.note || '',
      cookie: account.cookie || account.value || '',
      auto_confirm: account.auto_confirm || false,
      pause_duration: account.pause_duration || 0,
      username: account.username || '',
      // 密码永不回填（后端也不回传明文）；留空表示不修改
      login_password: '',
      showLoginPassword: false,
    });
    setActiveModal('edit');
  };

  /** 独立切换「显示浏览器」：走专用接口，立即生效，不受表单保存影响 */
  const handleToggleShowBrowser = async () => {
    if (!editingAccount) return;
    const next = !editingAccount.show_browser;
    setTogglingBrowser(true);
    try {
      await updateAccountShowBrowser(editingAccount.id, next);
      setEditingAccount({ ...editingAccount, show_browser: next });
      loadAccounts(true);
    } catch (e) {
      console.error('切换显示浏览器失败:', e);
      toast.error('切换失败，请重试');
    } finally {
      setTogglingBrowser(false);
    }
  };

  const openAIModal = async (account: AccountDetail) => {
    setEditingAccount(account);
    setSaving(true);
    try {
      const settings = await getAccountAISettings(account.id);
      setAiSettings({
        ai_enabled: settings.ai_enabled ?? false,
        max_discount_percent: settings.max_discount_percent ?? 10,
        max_discount_amount: settings.max_discount_amount ?? 100,
        max_bargain_rounds: settings.max_bargain_rounds ?? 3,
        custom_prompts: settings.custom_prompts ?? '',
      });
    } catch (e) {
      console.error('Failed to load AI settings:', e);
    } finally {
      setSaving(false);
    }
    setActiveModal('ai-settings');
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;
    setSaving(true);

    try {
      const promises: Promise<any>[] = [];

      // 更新备注
      if (editForm.remark !== (editingAccount.remark || editingAccount.note || '')) {
        promises.push(updateAccountRemark(editingAccount.id, editForm.remark));
      }

      // 更新Cookie
      if (editForm.cookie && editForm.cookie !== (editingAccount.cookie || editingAccount.value || '')) {
        promises.push(updateAccountCookie(editingAccount.id, editForm.cookie));
      }

      // 更新自动确认
      if (editForm.auto_confirm !== editingAccount.auto_confirm) {
        promises.push(updateAccountAutoConfirm(editingAccount.id, editForm.auto_confirm));
      }

      // 更新暂停时长
      if (editForm.pause_duration !== (editingAccount.pause_duration || 0)) {
        promises.push(updateAccountPauseDuration(editingAccount.id, editForm.pause_duration));
      }

      // 更新登录信息（只提交用户真正填写的内容，不提交 show_browser）
      // 密码框永远为空（后端不回传明文），只有输入了新密码才提交，留空=保持原密码不变
      const usernameChanged = editForm.username !== (editingAccount.username || '');
      const newPassword = editForm.login_password.trim();
      if (usernameChanged || newPassword) {
        const payload: { username?: string; login_password?: string } = {};
        if (usernameChanged) payload.username = editForm.username;
        if (newPassword) payload.login_password = newPassword;
        promises.push(updateAccountLoginInfo(editingAccount.id, payload));
      }

      await Promise.all(promises);
      setActiveModal(null);
      loadAccounts();
    } catch (error) {
      console.error('更新账号失败:', error);
      toast.error('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAISettings = async () => {
    if (!editingAccount) return;
    setSaving(true);

    try {
      await updateAccountAISettings(editingAccount.id, aiSettings);
      setActiveModal(null);
      loadAccounts();
    } catch (error) {
      console.error('更新AI设置失败:', error);
      toast.error('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const startQRLogin = async (openStandalone = true) => {
    // 重复点击时先清掉上一轮轮询，否则会叠出多个 interval
    stopQRPolling();
    // openStandalone=false 时（如「刷新凭证」弹窗内嵌扫码），不单独弹出扫码 Modal，
    // 二维码 inline 渲染在调用方弹窗里；openStandalone=true 时维持原行为（顶部「扫码添加新账号」/卡片「重新扫码」）
    if (openStandalone) setShowQRModal(true);
    setQrStatus('loading');
    setQrErrorMsg('');
    try {
      const res = await generateQRLogin();
      if (res.success && res.qr_code_url && res.session_id) {
        setQrCodeUrl(res.qr_code_url);
        setQrStatus('waiting');

        const sessionId = res.session_id;
        // 后端处理扫码 Cookie（要拉浏览器补齐 Cookie）可能耗时十几秒，
        // 期间会返回 processing；这里用 in-flight 标记避免轮询请求堆叠。
        let polling = false;
        qrPollTimerRef.current = setInterval(async () => {
          if (polling) return;
          polling = true;
          try {
            const statusRes = await checkQRLoginStatus(sessionId);
            const st = statusRes?.status;
            if (st === 'processing' || st === 'scanned') {
              // 手机已确认，后端正在完成登录 —— 明确给出 loading，别让界面卡在二维码上
              setQrStatus('processing');
            } else if (st === 'success' || st === 'already_processed') {
              stopQRPolling();
              setQrStatus('success');
              qrCloseTimerRef.current = setTimeout(() => {
                qrCloseTimerRef.current = null;
                setShowQRModal(false);
                setRefreshAccount(null); // 若扫码是从「刷新凭证」弹窗内发起的，一并关闭外层弹窗
                loadAccounts();
              }, 1000);
            } else if (st === 'expired' || st === 'error' || st === 'not_found') {
              stopQRPolling();
              setQrStatus('error');
              setQrErrorMsg(statusRes?.message || (st === 'expired' ? '二维码已过期' : '登录失败'));
            }
          } catch (err) {
            // 单次轮询失败不打断流程，等下一轮；超时/错误状态由后端返回
            console.warn('扫码状态轮询失败:', err);
          } finally {
            polling = false;
          }
        }, 3000);
      }
    } catch (e) {
      console.error('生成登录二维码失败:', e);
      setQrStatus('error');
    }
  };

  // 人工验证：开可见浏览器让真人过闲鱼风控（滑块/短信/人脸），完成后自动重连
  const stopManualVerifyPolling = () => {
    if (mvPollRef.current !== null) {
      clearInterval(mvPollRef.current);
      mvPollRef.current = null;
    }
  };

  const openManualVerify = async (account: AccountDetail, mode: 'repair' | 'refresh' = 'repair') => {
    setMvAccount(account);
    setMvMode(mode);
    setMvStatus('starting');
    setMvMessage('正在启动浏览器...');
    setMvWarning('');
    setMvChanged([]);
    stopManualVerifyPolling();
    try {
      const res = await startManualVerify(account.id, mode);
      if (res && res.success === false) {
        setMvStatus('failed');
        setMvMessage(res.message || '启动人工验证失败');
        toast.error(res.message || '启动人工验证失败');
        return;
      }
      let polling = false;
      mvPollRef.current = setInterval(async () => {
        if (polling) return;
        polling = true;
        try {
          const s = await getManualVerifyStatus(account.id);
          if (s && s.status) {
            setMvStatus(s.status);
            setMvMessage(s.message || '');
            setMvControlUrl(s.control_url || '');
              if (['success', 'failed', 'cancelled'].includes(s.status)) {
              stopManualVerifyPolling();
              if (s.status === 'success') {
                const changed: string[] = s.changed_fields || [];
                setMvChanged(changed);
                toast.success(changed.length
                  ? `刷新完成，更新了 ${changed.length} 项凭证字段，账号正在重连`
                  : '刷新完成（凭证无变化，可能原样续期），账号正在重连');
                // 留 2.5 秒让用户在弹窗里看到「本次刷出了什么」再自动关闭并刷新列表
                mvCloseTimerRef.current = setTimeout(() => {
                  mvCloseTimerRef.current = null;
                  setMvAccount(null);
                  loadAccounts();
                }, 2500);
              } else if (s.status === 'failed') {
                toast.error(s.message || '人工验证未通过');
              }
            }
          }
        } catch (e) {
          // 单次轮询失败不打断
        } finally {
          polling = false;
        }
      }, 2000);
    } catch (e: any) {
      setMvStatus('failed');
      setMvMessage(e?.message || '启动人工验证失败');
    }
  };

  const closeManualVerify = async () => {
    if (mvAccount && ['starting', 'waiting_user', 'reconnecting'].includes(mvStatus)) {
      try { await cancelManualVerify(mvAccount.id); } catch (e) { /* 忽略 */ }
    }
    if (mvCloseTimerRef.current !== null) {
      clearTimeout(mvCloseTimerRef.current);
      mvCloseTimerRef.current = null;
    }
    stopManualVerifyPolling();
    setMvAccount(null);
    setMvWarning('');
    setMvChanged([]);
    setMvControlUrl('');
    loadAccounts();
  };

  // 账号是否处于「需要人工干预」的状态：滑块失败停机 / 连接失败 / 重连中
  // 用于决定凭证维护入口走 repair（救火）还是 refresh（预防性保养）
  const isAccountBroken = (a: AccountDetail) =>
    !!a.needs_manual_verify || a.connection_state === 'failed' || a.connection_state === 'reconnecting';

  if (loading) return <div className="p-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#FFE815] animate-spin"/></div>;

  return (
    <div className="space-y-8 animate-fade-in relative">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">账号管理</h2>
          <p className="text-gray-500 mt-2 font-medium">管理您的闲鱼授权账号及设置。</p>
        </div>
        <button
            onClick={() => startQRLogin()}
            className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 transition-transform hover:scale-105 active:scale-95"
        >
          <QrCode className="w-5 h-5" />
          扫码添加新账号
        </button>
      </div>

      {/* Account Grid */}
      <div className="grid grid-cols-1 gap-6">
        {accounts.map((account) => (
          <div key={account.id} className="ios-card p-6 rounded-[2rem] flex items-center justify-between group hover:border-[#FFE815] transition-all duration-300">
            <div className="flex items-center gap-8">
              <div className="relative">
                <img
                  src={account.avatar_url}
                  alt="avatar"
                  className="w-20 h-20 rounded-3xl object-cover shadow-md ring-4 ring-white"
                />
                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${account.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {account.enabled && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-extrabold text-gray-900">{account.nickname || account.remark || `账号 ${account.id.substring(0,6)}...`}</h3>
                    <ConnectionBadge account={account} />
                    {account.ai_enabled && (
                        <span className="px-2.5 py-0.5 rounded-lg bg-purple-100 text-purple-700 text-xs font-bold flex items-center gap-1">
                          <Bot className="w-3 h-3" /> AI
                        </span>
                    )}
                </div>
                <p className="text-sm text-gray-500 font-medium mb-3">{account.remark || account.note || '暂无备注'}</p>
                <div className="flex gap-2">
                   {account.auto_confirm && <span className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5"><MessageSquare className="w-3 h-3"/> 自动回复</span>}
                   {/* 「人工接管静默时长」是低频配置，不常驻卡片；仅在编辑弹窗中配置，避免被误解为账号已暂停 */}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
                {!account.enabled && (
                  <button
                    onClick={async () => {
                      try {
                        await updateAccountStatus(account.id, true);
                        toast.success('账号已启用，正在重新连接');
                        loadAccounts();
                      } catch (e: any) {
                        toast.error(e?.message || '启用失败');
                      }
                    }}
                    className="p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors text-green-700 font-bold flex items-center gap-1.5 shadow-sm"
                    title="启用该账号并立即重新连接（同时清除「需重新扫码」停机登记）"
                  >
                    <Check className="w-5 h-5" />
                    启用并重连
                  </button>
                )}
                {account.needs_manual_verify && (
                  <button
                    onClick={() => startQRLogin()}
                    className="p-3 rounded-xl bg-[#FFE815] hover:bg-yellow-400 transition-colors text-gray-900 font-bold flex items-center gap-1.5 shadow-sm"
                    title="重新扫码登录（换新 Cookie）"
                  >
                    <QrCode className="w-5 h-5" />
                    重新扫码
                  </button>
                )}
                {/* 凭证维护入口（常驻显示）：
                    账号掉线/被风控 → repair 模式，红色「人工验证」救火；
                    账号仍在线     → refresh 模式，蓝色「刷新凭证」预防性保养。
                    说明：以前这个按钮只在掉线时才渲染，账号在线时想主动刷新根本点不到。 */}
                <button
                  onClick={() => {
                    // 改为弹出「凭证维护」二选一：优先扫码换 Cookie，扫码过不了再走人工验证兜底
                    setQrStatus('pending');
                    setQrErrorMsg('');
                    setRefreshAccount(account);
                  }}
                  className={`p-3 rounded-xl transition-colors font-bold flex items-center gap-1.5 shadow-sm ${
                    isAccountBroken(account)
                      ? 'bg-red-50 hover:bg-red-100 text-red-600'
                      : 'bg-sky-50 hover:bg-sky-100 text-sky-700'
                  }`}
                  title={isAccountBroken(account)
                    ? '人工验证：打开可见浏览器，手动过闲鱼的风控验证（滑块/短信）'
                    : '刷新凭证（预防性保养）：打开可见浏览器走一遍人工验证流程，立刻换一份全新 Cookie 并自动重连生效。保养期间该账号会短暂断开（约 1 分钟）。'}
                >
                  <ShieldCheck className="w-5 h-5" />
                  {isAccountBroken(account) ? '人工验证' : '刷新凭证'}
                </button>
                <button
                    onClick={() => openEditModal(account)}
                    className="p-3 rounded-xl hover:bg-gray-100 transition-colors text-gray-600"
                    title="编辑账号"
                >
                    <Edit2 className="w-5 h-5" />
                </button>
                <button
                    onClick={() => openAIModal(account)}
                    className="p-3 rounded-xl hover:bg-purple-100 transition-colors text-purple-600"
                    title="AI设置"
                >
                    <Bot className="w-5 h-5" />
                </button>
                <button
                    onClick={() => handleDelete(account.id)}
                    className="p-3 rounded-xl hover:bg-red-100 transition-colors text-red-500"
                >
                    <Trash2 className="w-5 h-5" />
                </button>
            </div>
          </div>
        ))}

        {accounts.length === 0 && (
            <div className="ios-card p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">暂无账号</h3>
                <p className="text-gray-500 mt-1">请点击右上角扫码添加您的闲鱼账号</p>
            </div>
        )}
      </div>

      {/* 刷新凭证弹窗：扫码换 Cookie（优先）+ 人工验证（过风控兜底）二选一 */}
      {refreshAccount && createPortal(
          <div className="modal-overlay-centered">
              <div className="modal-container" style={{maxWidth: '26rem'}}>
                  <div className="modal-header">
                      <div>
                          <h3 className="text-2xl font-extrabold text-gray-900">刷新凭证</h3>
                          <p className="text-gray-500 text-sm mt-1 font-medium">优先扫码换新 Cookie；若账号被风控拦截，请走下方人工验证。</p>
                      </div>
                      <button
                        onClick={() => { stopQRPolling(); setRefreshAccount(null); }}
                        className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                      >
                          <X className="w-5 h-5 text-gray-600" />
                      </button>
                  </div>

                  <div className="modal-body space-y-4">
                      {/* ① 扫码换凭证 */}
                      <div className="rounded-2xl border border-gray-100 p-5">
                          <div className="flex items-center gap-2 mb-1">
                              <QrCode className="w-5 h-5 text-gray-900" />
                              <h4 className="text-lg font-extrabold text-gray-900">① 扫码换凭证（推荐）</h4>
                          </div>
                          <p className="text-xs text-gray-500 mb-4 leading-relaxed">用闲鱼 / 淘宝 APP 扫一下，后端自动补齐并回灌新 Cookie，账号会自动重连，无需操作服务器浏览器。</p>
                          {renderQRBox(() => startQRLogin(false))}
                          <p className="text-[11px] text-gray-400 font-medium bg-gray-50 py-2 rounded-xl mt-3">二维码有效期为5分钟，请尽快扫码。</p>
                      </div>

                      {/* ② 人工验证兜底 */}
                      <div className="rounded-2xl border border-gray-100 p-5">
                          <div className="flex items-center gap-2 mb-1">
                              <ShieldCheck className="w-5 h-5 text-red-500" />
                              <h4 className="text-lg font-extrabold text-gray-900">② 改用人工验证（过风控）</h4>
                          </div>
                          <p className="text-xs text-gray-500 mb-4 leading-relaxed">账号被滑块 / 人脸风控拦截时，扫码无法解决。点此打开服务器浏览器远程投屏，手动过一次验证。</p>
                          <button
                              onClick={() => {
                                const acc = refreshAccount;
                                stopQRPolling();
                                setRefreshAccount(null);
                                openManualVerify(acc, isAccountBroken(acc) ? 'repair' : 'refresh');
                              }}
                              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                          >
                              <ShieldCheck className="w-5 h-5" />
                              打开人工验证
                          </button>
                      </div>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {/* QR Code Modal（独立：顶部「扫码添加新账号」/ 卡片「重新扫码」使用） */}
      {showQRModal && createPortal(
          <div className="modal-overlay-centered">
              <div className="modal-container" style={{maxWidth: '24rem'}}>
                  <button
                    onClick={closeQRModal}
                    className="self-end p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors mb-6"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>

                  <div className="modal-body">
                      <div className="text-center">
                          <h3 className="text-2xl font-extrabold text-gray-900 mb-2">扫码登录</h3>
                          <p className="text-gray-500 mb-8 font-medium">请打开闲鱼APP扫描下方二维码</p>

                          {renderQRBox(() => startQRLogin(true))}

                          <p className="text-xs text-gray-400 font-medium bg-gray-50 py-2 rounded-xl">二维码有效期为5分钟，请尽快扫码。</p>
                      </div>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {/* 人工验证弹窗（可见浏览器过风控） */}
      {mvAccount && createPortal(
          <div className="modal-overlay-centered">
              <div className="modal-container" style={{maxWidth: '34rem'}}>
                  <div className="modal-header">
                      <div>
                          <h3 className="text-2xl font-extrabold text-gray-900">
                              {mvMode === 'refresh' ? '刷新登录凭证' : '人工验证'}
                          </h3>
                          <p className="text-gray-500 text-sm mt-1 font-medium">
                              {mvMode === 'refresh'
                                  ? '预防性保养：账号当前在线，主动换一份全新凭证，降低突然掉线的风险'
                                  : '闲鱼对该账号触发了风控，需要真人完成一次验证'}
                          </p>
                      </div>
                      <button onClick={closeManualVerify} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                          <X className="w-5 h-5 text-gray-600" />
                      </button>
                  </div>

                  <div className="modal-body">
                      <div className="space-y-4">
                          {mvMode === 'refresh' ? (
                              <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside bg-gray-50 p-4 rounded-2xl">
                                  <li>{mvControlUrl ? '下方是服务器浏览器的实时画面（远程投屏），已自动带入账号当前登录状态，直接在画面里操作即可。' : '系统已打开一个浏览器窗口，并自动带入了账号当前的登录状态（所以一打开就是已登录——这是正常的）。'}</li>
                                  <li>在浏览器里点「消息」或「我的」随便操作几下，让闲鱼重新下发一次凭证。</li>
                                  <li>想彻底换新：在浏览器里「退出登录 → 用手机短信验证码重新登录」，这样拿到的一定是全新凭证。</li>
                                  <li>完成后点下方「验证完成，开始重连」，系统会读取新凭证、校验后重连账号。</li>
                              </ol>
                          ) : (
                              <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside bg-gray-50 p-4 rounded-2xl">
                                  <li>{mvControlUrl ? '下方是服务器浏览器的实时画面（远程投屏），已自动带入账号登录状态，直接在画面里完成验证即可。' : '系统已打开一个浏览器窗口，并自动带入了账号当前的登录状态（所以一打开可能就是已登录——这是正常的）。'}</li>
                                  <li>在浏览器里点「消息」或「我的」随便操作一下：若闲鱼弹出滑块 / 短信 / 人脸验证，完成它；若全程没弹任何验证，建议「退出登录 → 用手机短信验证码重新登录」，这一步最能解除风控。</li>
                                  <li>完成后点下方「验证完成，开始重连」，系统会自动读取新凭证并重连账号。</li>
                              </ol>
                          )}

                          {/* 远程投屏：服务器浏览器实时画面，鼠标直接操作（部署在无头/远程服务器时替代看不到的本地浏览器） */}
                          {mvControlUrl && (
                              <div className="rounded-2xl overflow-hidden border border-gray-200 bg-black">
                                  <div className="text-[11px] text-gray-500 px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                      <span>服务器浏览器实时画面（鼠标直接操作）</span>
                                      <a href={mvControlUrl} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline">新窗口打开</a>
                                  </div>
                                  <iframe
                                      src={mvControlUrl}
                                      title="远程浏览器"
                                      className="w-full block bg-black"
                                      style={{ height: '340px', border: 'none' }}
                                  />
                              </div>
                          )}

                          {mvMode === 'refresh' && (
                              <div className="flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50">
                                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-amber-800 leading-relaxed">
                                      保养期间该账号会短暂断开（约 1 分钟，用于让新凭证生效），
                                      这段时间进来的消息会在重连后照常处理，不会丢单。
                                      若浏览器里读到的登录态不如当前账号持有的，系统会直接放弃写入、保持原凭证不变。
                                  </p>
                              </div>
                          )}

                          <div className="flex items-start gap-3 p-4 rounded-2xl border border-gray-100">
                              {['starting', 'reconnecting'].includes(mvStatus)
                                  ? <Loader2 className="w-5 h-5 text-[#FFE815] animate-spin mt-0.5" />
                                  : mvStatus === 'failed'
                                      ? <span className="w-5 h-5 mt-0.5 text-red-500 font-bold">!</span>
                                      : <Check className="w-5 h-5 text-green-600 mt-0.5" />}
                              <div>
                                  <p className={`font-bold ${mvStatus === 'failed' ? 'text-red-600' : 'text-gray-900'}`}>
                                      {mvStatus === 'starting' && '正在启动浏览器…'}
                                      {mvStatus === 'waiting_user' && '等待你在浏览器中完成验证'}
                                      {mvStatus === 'reconnecting' && '正在重连账号…'}
                                      {mvStatus === 'failed' && '验证未通过'}
                                      {mvStatus === 'success' && '验证成功'}
                                      {mvStatus === 'cancelled' && '已取消'}
                                  </p>
                                  {mvMessage && <p className="text-xs text-gray-500 mt-1 leading-relaxed">{mvMessage}</p>}
                              </div>
                          </div>

                          {/* 刷新成功：把「这次刷出了什么」摊开给用户核对，而不是只给一句「成功」 */}
                          {mvStatus === 'success' && (
                              <div className="flex items-start gap-3 p-4 rounded-2xl border border-sky-200 bg-sky-50">
                                  <RefreshCw className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                      <p className="font-bold text-sky-800 text-sm">
                                          {mvChanged.length > 0
                                              ? `已刷新 ${mvChanged.length} 项凭证字段`
                                              : '凭证字段无变化（可能原样续期）'}
                                      </p>
                                      {mvChanged.length > 0 && (
                                          <p className="text-xs text-sky-700 mt-1 leading-relaxed">
                                              {mvChanged.join('、')}
                                          </p>
                                      )}
                                  </div>
                              </div>
                          )}

                          {/* 后端检测到「浏览器里其实没登录成功」的警告：只提示，不拦截 */}
                          {mvWarning && (
                              <div className="flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50">
                                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                      <p className="font-bold text-amber-800 text-sm">可能还没登录成功</p>
                                      <p className="text-xs text-amber-700 mt-1 leading-relaxed">{mvWarning}</p>
                                      <p className="text-xs text-amber-600 mt-2 leading-relaxed">
                                          已按你的确认继续重连。若重连失败，可以关掉本窗口重新点「人工验证」，在浏览器里「退出登录 → 短信验证码重登」后再点验证完成。
                                      </p>
                                  </div>
                              </div>
                          )}
                      </div>
                  </div>

                  <div className="modal-footer flex gap-3 justify-end">
                      <button
                          onClick={closeManualVerify}
                          className="px-5 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                          关闭
                      </button>
                      <button
                          disabled={mvStatus !== 'waiting_user'}
                          onClick={async () => {
                              if (!mvAccount) return;
                              const r = await confirmManualVerify(mvAccount.id);
                              setMvStatus('reconnecting');
                              setMvMessage('已收到确认，正在读取Cookie并重连...');
                              // 后端检测到浏览器里其实没登录成功：只提示，不拦截（用户仍可继续）
                              const w = (r && (r.warning || r.data?.warning)) || '';
                              setMvWarning(w);
                              if (w) toast.warning(w);
                          }}
                          className="px-5 py-2.5 rounded-xl font-bold bg-[#FFE815] text-gray-900 hover:bg-yellow-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                          验证完成，开始重连
                      </button>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {/* 编辑账号弹窗 */}
      {activeModal === 'edit' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900">编辑账号</h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 账号ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">账号ID</label>
                <input
                  type="text"
                  value={editingAccount.id}
                  disabled
                  className="w-full ios-input px-4 py-3 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">备注</label>
                <input
                  type="text"
                  value={editForm.remark}
                  onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
                  placeholder="为账号添加备注"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
              </div>

              {/* Cookie */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Cookie</label>
                <textarea
                  value={editForm.cookie}
                  onChange={(e) => setEditForm({ ...editForm, cookie: e.target.value })}
                  placeholder="更新账号Cookie"
                  className="w-full ios-input px-4 py-3 rounded-xl h-32 resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">当前Cookie长度: {editForm.cookie.length} 字符</p>
              </div>

              {/* 自动确认收货 */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    自动确认收货
                  </div>
                  <div className="text-xs text-gray-500">自动点击确认收货按钮</div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, auto_confirm: !editForm.auto_confirm })}
                  className={`w-14 h-8 rounded-full transition-colors duration-300 relative ${
                    editForm.auto_confirm ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      editForm.auto_confirm ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* 暂停时长 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  人工接管静默时长（分钟）
                </label>
                <input
                  type="number"
                  value={editForm.pause_duration}
                  onChange={(e) => setEditForm({ ...editForm, pause_duration: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  max="120"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
                <p className="text-xs text-gray-500 mt-1">
                  你手动回复买家后，该会话的自动回复会静默这么久，避免机器人插话。填 0 表示不静默。范围 0~120 分钟。
                </p>
              </div>

              {/* 登录信息 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  登录信息
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">用户名</label>
                    <input
                      type="text"
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      placeholder="闲鱼账号/手机号"
                      className="w-full ios-input px-4 py-3 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      登录密码
                      {editingAccount?.has_login_password && (
                        <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-lg bg-green-100 text-green-700">已保存</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={editForm.showLoginPassword ? 'text' : 'password'}
                        value={editForm.login_password}
                        onChange={(e) => setEditForm({ ...editForm, login_password: e.target.value })}
                        placeholder={editingAccount?.has_login_password ? '已保存，留空表示不修改' : '用于自动登录（可留空）'}
                        className="w-full ios-input px-4 py-3 rounded-xl pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, showLoginPassword: !editForm.showLoginPassword })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {editForm.showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      出于安全考虑不回显密码。只改账号时密码框留空即可，不会清空已保存的密码。
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900 flex items-center gap-2">
                        显示浏览器
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${editingAccount?.show_browser ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {editingAccount?.show_browser ? '已开启' : '已关闭'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {editingAccount?.show_browser
                          ? '开启时滑块窗口可见，可人工拖动；建议保持开启'
                          : '关闭后无头运行，滑块大概率被识别而失败'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">此开关独立保存，修改其它字段不会影响它</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleShowBrowser}
                      disabled={togglingBrowser}
                      className={`w-14 h-8 rounded-full transition-colors duration-300 relative flex-shrink-0 disabled:opacity-60 ${
                        editingAccount?.show_browser ? 'bg-[#FFE815]' : 'bg-gray-300'
                      }`}
                      title={editingAccount?.show_browser ? '点击关闭' : '点击开启'}
                    >
                      <span
                        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                          editingAccount?.show_browser ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI设置弹窗 */}
      {activeModal === 'ai-settings' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                  <Bot className="w-6 h-6 text-purple-500" />
                  AI助手设置
                </h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 启用AI */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-500" />
                    启用AI自动回复
                  </div>
                  <div className="text-xs text-gray-500">AI将自动处理买家的砍价消息</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAiSettings({ ...aiSettings, ai_enabled: !aiSettings.ai_enabled })}
                  className={`w-14 h-8 rounded-full transition-colors duration-300 relative ${
                    aiSettings.ai_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      aiSettings.ai_enabled ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* 砍价策略 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">砍价策略</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣比例 (%)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_percent}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_percent: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：10表示最多降价10%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣金额 (元)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_amount}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_amount: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：100表示最多降价100元</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大砍价轮次</label>
                    <input
                      type="number"
                      value={aiSettings.max_bargain_rounds}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_bargain_rounds: parseInt(e.target.value) || 1 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="1"
                      max="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">买家最多可以砍价的次数</p>
                  </div>
                </div>
              </div>

              {/* 自定义提示词 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">自定义提示词（可选）</label>
                <textarea
                  value={aiSettings.custom_prompts}
                  onChange={(e) => setAiSettings({ ...aiSettings, custom_prompts: e.target.value })}
                  placeholder="输入自定义的AI回复规则或风格指引...&#10;&#10;例如：回复时保持礼貌专业、使用简洁的语言、强调产品质量等"
                  className="w-full ios-input px-4 py-3 rounded-xl h-40 resize-none"
                />
              </div>

              {/* AI如何工作 */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  AI如何工作
                </h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• 自动识别买家的砍价请求</li>
                  <li>• 根据设定的策略智能回复</li>
                  <li>• 在合理范围内同意降价或礼貌拒绝</li>
                  <li>• 保持专业友好的沟通风格</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveAISettings}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AccountList;
